import type { ReactNode } from "react";

// The shared "nothing here yet" state (Session AQT, Phase 1 design system foundation). Before
// this, an empty list was either a bare `text-ele-body opacity-70` line hand-copied across 38+
// files with no consistent shape, or a locally-repeated `emptyText` prop on one-off components
// (JobPhotoGallery, PortalHistory's HistorySection) each with their own styling. One component,
// one look: a message, an optional hint (CompanyDocumentsPage's "Upload the H&S policy..." style
// of actionable guidance), and an optional action button.

export interface EmptyStateProps {
  message: string;
  hint?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
  /** No border or panel, py-4, left-aligned: for a section inside a card or a table body (Session AUJ). */
  compact?: boolean;
  /** Set "status" when the empty state replaces a list that was just filtered to nothing (Session AUJ). */
  role?: "status";
}

//
// Rule (Session AUJ, spec 3.4): a list that is empty says so with EmptyState; a list filtered to
// nothing says "No <things> match" with the search term, also through EmptyState (role="status").
// Inside a <table>, render it in one full-width <td colSpan> with `compact`.
export function EmptyState({ message, hint, action, icon, className = "", compact = false, role }: EmptyStateProps) {
  const shape = compact
    ? "flex flex-col items-start gap-2 py-4 text-left"
    : "flex flex-col items-center gap-2 rounded-ele border border-ele-hairline bg-ele-surface px-6 py-10 text-center";
  return (
    <div role={role} className={`${shape} ${className}`.trim()}>
      {icon}
      <p className="text-sm text-ele-body opacity-70">{message}</p>
      {hint != null && <p className="max-w-sm text-xs text-ele-body opacity-60">{hint}</p>}
      {action != null && <div className="mt-2">{action}</div>}
    </div>
  );
}
